import Colors from '@dynatrace/strato-design-tokens/colors';

import {
  DEFAULT_DATA_POINT_RADIUS,
  DEFAULT_HIGHLIGHTED_OPACITY,
} from '../../constants.js';
import { DataPoint } from '../../types/data-point.js';
import { RoundRectCanvas } from '../canvas/components/RoundRectCanvas.js';
import { SymbolCanvas } from '../canvas/components/SymbolCanvas.js';
import type { PointShapeType } from '../shape/type.js';
import { DATA_POINT_OUTER_RADIUS } from './constants.js';
import { getStrokeColor, getOuterCircleSize } from './utils/data-point.js';

interface CanvasGroupDataPointsProps {
  /** DataPoints belonging to the same group */
  points: DataPoint[];
  /** Closest point to the mouse position & the x and y position of the tooltip */
  closestPoint?: DataPoint;
  /** axis where the value coordinates belongs to */
  valueAxis?: 'x' | 'y';
  shape?: PointShapeType;
}

const OuterCircleDataPoint = (props: CanvasGroupDataPointsProps) => {
  const { points, closestPoint, valueAxis = 'y', shape } = props;
  const { x, y, width, height } = getOuterCircleSize(points, valueAxis);
  const strokeColor = getStrokeColor(points, closestPoint);
  const isGroup = points.length > 1;

  return isGroup ? (
    <RoundRectCanvas
      x={x}
      y={y}
      width={width}
      height={height}
      color={Colors.Background.Base.Default}
      opacity={DEFAULT_HIGHLIGHTED_OPACITY}
      borderRadius={7}
      borderColor={strokeColor}
      borderWidth={1}
    />
  ) : (
    <SymbolCanvas
      x={points[0]?.x}
      y={points[0]?.y}
      color={Colors.Background.Base.Default}
      opacity={DEFAULT_HIGHLIGHTED_OPACITY}
      r={DATA_POINT_OUTER_RADIUS}
      strokeColor={strokeColor}
      strokeWidth={1}
      shape={shape}
    />
  );
};

/**
 * Component for rendering a single dataPoint or a group of dataPoints.
 */
export const DataPointCanvas = (props: CanvasGroupDataPointsProps) => {
  const { points, valueAxis = 'y', shape } = props;

  const paintedPositions = new Set();

  return (
    <>
      {/** Outer circle */}
      {points.length > 0 && <OuterCircleDataPoint {...props} />}
      {/** Inner circle */}
      {points.map((point, index) => {
        // Use the x or y position (depending on the layout) as a key since it doesn't make sense to draw points on top of each other.
        const key = Math.round(point[valueAxis]);
        // TODO: When is nothing set on a series id?
        // Do not paint a circle again if we already painted one at the exact same position.
        if (paintedPositions.has(key) || point.id === 'nothing') {
          return null;
        }
        paintedPositions.add(key);

        return (
          <SymbolCanvas
            key={index}
            x={point.x}
            y={point.y}
            color={point.color.Default}
            r={DEFAULT_DATA_POINT_RADIUS}
            shape={shape}
          />
        );
      })}
    </>
  );
};
